import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import { merchantService, ApiError } from '../services';

const MerchantDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const merchant = location.state?.merchant;
  const [detailMerchant, setDetailMerchant] = useState(merchant || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep local merchant details in sync with route changes
  useEffect(() => {
    setDetailMerchant(merchant || null);
  }, [id, merchant]);

  // Fetch merchant details if not provided in state
  useEffect(() => {
    if (!detailMerchant && id) {
      const fetchMerchantDetails = async () => {
        setLoading(true);
        setError(null);

        try {
          const response = await merchantService.getFormattedMerchantDetails(
            id
          );
          if (response && response.data) {
            setDetailMerchant(response.data);
          }
        } catch (err) {
          console.error('Failed to fetch merchant details:', err);
          if (err instanceof ApiError) {
            setError(`Failed to load merchant details: ${err.message}`);
          } else {
            setError('Failed to load merchant details. Please try again.');
          }
        } finally {
          setLoading(false);
        }
      };

      fetchMerchantDetails();
    }
  }, [detailMerchant, id]);

  // Redirect to merchants list if no merchant data is provided and fetch failed
  useEffect(() => {
    if (!loading && !detailMerchant && error) {
      navigate(ROUTES.MERCHANTS, { replace: true });
    }
  }, [detailMerchant, navigate, loading, error]);

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            ></circle>
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            ></path>
          </svg>
          <span className='text-gray-600'>Loading merchant details...</span>
        </div>
      </div>
    );
  }

  if (!detailMerchant) {
    return null; // Will redirect
  }

  return (
    <div>
      <PageHeader
        title={`Merchant Details: ${detailMerchant.companyName}`}
        subtitle='View and manage merchant information'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Merchants', href: ROUTES.MERCHANTS },
          { name: detailMerchant.companyName },
        ]}
      />

      {/* Error Display */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
          <div className='flex items-center'>
            <svg
              className='w-5 h-5 text-red-400 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <p className='text-sm text-red-700'>{error}</p>
          </div>
        </div>
      )}

      {/* Merchant Information Card */}
      <div className='bg-white shadow-sm border border-gray-200 rounded-lg p-6 mb-6'>
        <div className='flex items-start justify-between mb-6'>
          <div>
            <h2 className='text-2xl font-bold text-gray-900'>
              {detailMerchant.companyName}
            </h2>
            <p className='text-sm text-gray-500 mt-1'>
              Merchant ID: {detailMerchant.id}
            </p>
          </div>
          <div className='flex items-center space-x-2'>
            <span
              className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                detailMerchant.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {detailMerchant.isActive ? 'Active' : 'Inactive'}
            </span>
            {detailMerchant.isPaid && (
              <span className='inline-flex px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800'>
                Paid
              </span>
            )}
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {/* Basic Information */}
          <div className='space-y-4'>
            <h3 className='text-lg font-medium text-gray-900'>
              Basic Information
            </h3>
            <div className='space-y-2'>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Company Name
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.companyName}
                </p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Email
                </label>
                <p className='text-sm text-gray-900'>{detailMerchant.email}</p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Phone
                </label>
                <p className='text-sm text-gray-900'>{detailMerchant.phone}</p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Sector
                </label>
                <p className='text-sm text-gray-900'>{detailMerchant.sector}</p>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className='space-y-4'>
            <h3 className='text-lg font-medium text-gray-900'>Location</h3>
            <div className='space-y-2'>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Address
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.address}
                </p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  ZIP Code
                </label>
                <p className='text-sm text-gray-900'>{detailMerchant.zip}</p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Country
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.country}
                </p>
              </div>
              {(detailMerchant.lat !== 0 || detailMerchant.lon !== 0) && (
                <div>
                  <label className='text-sm font-medium text-gray-500'>
                    Coordinates
                  </label>
                  <p className='text-sm text-gray-900'>
                    {detailMerchant.lat}, {detailMerchant.lon}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Business Information */}
          <div className='space-y-4'>
            <h3 className='text-lg font-medium text-gray-900'>
              Business Details
            </h3>
            <div className='space-y-2'>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Balance
                </label>
                <p className='text-sm text-gray-900 font-semibold'>
                  ${detailMerchant.balance}
                </p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Career Points
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.careerPoint}
                </p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Join Date
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.joinDate}
                </p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Last Modified
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.lastModified}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        {(detailMerchant.referralCode !== 'N/A' || detailMerchant.googleID) && (
          <div className='mt-6 pt-6 border-t border-gray-200'>
            <h3 className='text-lg font-medium text-gray-900 mb-4'>
              Additional Information
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {detailMerchant.referralCode !== 'N/A' && (
                <div>
                  <label className='text-sm font-medium text-gray-500'>
                    Referral Code
                  </label>
                  <p className='text-sm text-gray-900 font-mono'>
                    {detailMerchant.referralCode}
                  </p>
                </div>
              )}
              {detailMerchant.googleID && (
                <div>
                  <label className='text-sm font-medium text-gray-500'>
                    Google Place ID
                  </label>
                  <p className='text-sm text-gray-900 font-mono'>
                    {detailMerchant.googleID}
                  </p>
                </div>
              )}
              {detailMerchant.voucher && (
                <div>
                  <label className='text-sm font-medium text-gray-500'>
                    Voucher
                  </label>
                  <p className='text-sm text-gray-900 font-mono'>
                    {detailMerchant.voucher.substring(0, 20)}...
                  </p>
                </div>
              )}
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Downline Count
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.downline}
                </p>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>
                  Partner Count
                </label>
                <p className='text-sm text-gray-900'>
                  {detailMerchant.partner}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className='flex justify-end space-x-3'>
        <button
          onClick={() => navigate(ROUTES.MERCHANTS)}
          className='px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
        >
          Back to Merchants
        </button>
        {/* <button
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={() => {
            // TODO: Implement edit functionality
            console.log('Edit merchant:', detailMerchant);
          }}
        >
          Edit Merchant
        </button> */}
      </div>
    </div>
  );
};

export default MerchantDetailPage;
